/**
 * Procurement API client.
 * Calls relative `/api` endpoints with session cookies.
 */

import { apiFetch } from '../apiFetch'
import type {
  PurchaseOrderDto,
  PoLineDto,
  PoUomWarningDto,
  PoStatus,
  PricePreviewPayload,
  PricePreviewResult,
  ResolvePricePayload,
  ResolvedPurchasePrice,
  CreatePoPayload,
  ReceivePoPayload,
  ReceiptDto,
  RecordShortPayload,
  PurchaseRequestDto,
  PrStatus,
  CreatePrPayload,
  ConvertPrPayload,
  PurchaseSuggestionDto,
  SuggestionStatus,
  RefreshSuggestionsResult,
  UomFactorDto,
  DocumentChangeLogDto,
  PatchPoHeaderPayload,
  PatchPoLinePayload,
  CreatePoLinePayload,
  PatchPrHeaderPayload,
  CreatePrLinePayload,
  PatchPrLinePayload,
  PoRefreshPricesPreview,
  NegotiatedPriceDto,
  CreateNegotiatedPricePayload,
} from './types'

export type ProcurementApiErrorResponse = {
  ok: false
  error: { code: string; message: string; status?: number; details?: Record<string, unknown> }
}

export class ProcurementApiClientError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, unknown>

  constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ProcurementApiClientError'
    this.code = code
    this.status = status
    this.details = details
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfError(res: Response, body: unknown, fallbackMessage: string): void {
  if (res.ok) return
  const err = body as ProcurementApiErrorResponse
  const code = err?.error?.code ?? 'HTTP_ERROR'
  const message = err?.error?.message ?? `${fallbackMessage} (${res.status})`
  const status = err?.error?.status ?? res.status
  throw new ProcurementApiClientError(code, message, status, err?.error?.details)
}

// ─── PURCHASE ORDERS ─────────────────────────────────────────────

export async function listPurchaseOrders(filter?: {
  status?: PoStatus
  supplierId?: string
  search?: string
  includeArchived?: boolean
  archivedOnly?: boolean
  limit?: number
  offset?: number
}): Promise<PurchaseOrderDto[]> {
  const params = new URLSearchParams()
  if (filter?.status) params.set('status', filter.status)
  if (filter?.supplierId) params.set('supplier_id', filter.supplierId)
  if (filter?.search) params.set('search', filter.search)
  if (filter?.includeArchived) params.set('include_archived', 'true')
  if (filter?.archivedOnly) params.set('archived_only', 'true')
  if (filter?.limit != null) params.set('limit', String(filter.limit))
  if (filter?.offset != null) params.set('offset', String(filter.offset))

  const qs = params.toString()
  const res = await apiFetch(`/api/purchase-orders${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải danh sách PO')
  return (body as { data: PurchaseOrderDto[] }).data
}

export async function getPurchaseOrder(id: string): Promise<PurchaseOrderDto | null> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function getPurchaseOrderUomWarnings(id: string): Promise<PoUomWarningDto[]> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/uom-warnings`)
  const body = await parseJson<{ ok: boolean; data: PoUomWarningDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể kiểm tra cảnh báo quy đổi đơn vị cho PO ${id}`)
  return (body as { data: PoUomWarningDto[] }).data
}

export async function previewPoRefreshPrices(id: string, lineIds?: string[]): Promise<PoRefreshPricesPreview> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/refresh-prices/preview`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(lineIds ? { line_ids: lineIds } : {}),
  })
  const body = await parseJson<{ data: PoRefreshPricesPreview }>(res)
  throwIfError(res, body, 'Không thể xem trước cập nhật giá')
  return body.data
}

export async function applyPoRefreshPrices(id: string, previewHash: string, lineIds?: string[]): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/refresh-prices/apply`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preview_hash: previewHash, ...(lineIds ? { line_ids: lineIds } : {}) }),
  })
  const body = await parseJson<{ data: PurchaseOrderDto }>(res)
  throwIfError(res, body, 'Không thể áp dụng cập nhật giá')
  return body.data
}

export async function createNegotiatedPrice(poId:string,lineId:string,payload:CreateNegotiatedPricePayload):Promise<NegotiatedPriceDto>{
  const res=await apiFetch(`/api/purchase-orders/${encodeURIComponent(poId)}/lines/${encodeURIComponent(lineId)}/negotiated-prices`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)})
  const body=await parseJson<{data:NegotiatedPriceDto}>(res)
  throwIfError(res,body,'Không tạo được giá thương lượng')
  return body.data
}
export async function confirmNegotiatedPrice(id:string):Promise<NegotiatedPriceDto>{
  const res=await apiFetch(`/api/negotiated-prices/${encodeURIComponent(id)}/confirm`,{method:'POST',headers:{'Content-Type':'application/json'},body:'{}'})
  const body=await parseJson<{data:NegotiatedPriceDto}>(res)
  throwIfError(res,body,'Không xác nhận được giá thương lượng')
  return body.data
}
export async function listPoNegotiatedPrices(poId:string):Promise<NegotiatedPriceDto[]>{
  const res=await apiFetch(`/api/purchase-orders/${encodeURIComponent(poId)}/negotiated-prices`)
  const body=await parseJson<{data:NegotiatedPriceDto[]}>(res)
  throwIfError(res,body,'Không tải được giá thương lượng')
  return body.data
}
export async function listSupplierNegotiatedPrices(supplierId:string):Promise<NegotiatedPriceDto[]>{
  const res=await apiFetch(`/api/suppliers/${encodeURIComponent(supplierId)}/negotiated-prices`)
  const body=await parseJson<{data:NegotiatedPriceDto[]}>(res)
  throwIfError(res,body,'Không tải được giá thương lượng của NCC')
  return body.data
}

export async function previewPrice(payload: PricePreviewPayload): Promise<PricePreviewResult> {
  const res = await apiFetch('/api/purchase-orders/price-preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: PricePreviewResult } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Lỗi tính giá preview')
  return (body as { data: PricePreviewResult }).data
}

export async function resolvePriceApi(payload: ResolvePricePayload): Promise<ResolvedPurchasePrice> {
  const res = await apiFetch('/api/pricing/resolve-price', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: ResolvedPurchasePrice } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Lỗi tính giá server')
  return (body as { data: ResolvedPurchasePrice }).data
}

export async function resolvePriceBatchApi(items: ResolvePricePayload[]): Promise<ResolvedPurchasePrice[]> {
  const res = await apiFetch('/api/pricing/resolve-batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  })
  const body = await parseJson<{ ok: boolean; data: ResolvedPurchasePrice[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Lỗi tính giá batch server')
  return (body as { data: ResolvedPurchasePrice[] }).data
}

export async function createPurchaseOrder(
  payload: CreatePoPayload,
): Promise<PurchaseOrderDto> {
  const res = await apiFetch('/api/purchase-orders', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tạo đơn mua hàng')
  return (body as { data: PurchaseOrderDto }).data
}

export async function submitPurchaseOrder(id: string): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/submit`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể gửi duyệt PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function approvePurchaseOrder(
  id: string,
  payload?: { reason?: string },
): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload ?? {}),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể duyệt PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function sendPurchaseOrder(id: string): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/send`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể gửi PO ${id} cho NCC`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function cancelPurchaseOrder(
  id: string,
  reason: string,
): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể hủy PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

// ─── PO DIRECT EDITING & REOPEN ──────────────────────────────────

export async function patchPurchaseOrder(
  id: string,
  payload: PatchPoHeaderPayload,
): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chỉnh sửa PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function patchPoLine(
  id: string,
  lineId: string,
  payload: PatchPoLinePayload,
): Promise<PoLineDto> {
  const res = await apiFetch(
    `/api/purchase-orders/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  )
  const body = await parseJson<{ ok: boolean; data: PoLineDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chỉnh sửa dòng ${lineId} của PO ${id}`)
  return (body as { data: PoLineDto }).data
}

export async function createPoLine(
  id: string,
  payload: CreatePoLinePayload,
): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/lines`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể thêm dòng vào PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function deletePoLine(
  id: string,
  lineId: string,
  reason: string,
): Promise<PurchaseOrderDto> {
  const res = await apiFetch(
    `/api/purchase-orders/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
  )
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể xóa dòng ${lineId} của PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function reopenPurchaseOrder(
  id: string,
  reason: string,
): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/reopen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể khôi phục PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function reopenPurchaseOrderForEdit(
  id: string,
  reason: string,
): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/reopen-for-edit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể mở lại PO ${id} để sửa`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function getPoHistory(id: string): Promise<DocumentChangeLogDto[]> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/history`)
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải lịch sử thay đổi của PO ${id}`)
  return (body as { data: DocumentChangeLogDto[] }).data
}

export async function archivePurchaseOrder(id: string): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể lưu trữ PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export async function unarchivePurchaseOrder(id: string): Promise<PurchaseOrderDto> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/unarchive`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseOrderDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể bỏ lưu trữ PO ${id}`)
  return (body as { data: PurchaseOrderDto }).data
}

export interface PoReceiptHistoryRow {
  id: string
  receipt_id: string
  receipt_code: string
  received_at: string
  received_by: string
  receipt_status: string
  po_id: string
  po_code: string
  po_delivery_date: string | null
  po_status: PoStatus
  supplier_id: string
  supplier_name: string
  supplier_code: string
  material_id: string
  material_code: string
  material_name: string
  po_line_id: string
  order_qty: string
  order_uom: string
  received_qty: string
  short_qty: string
  short_resolution: string | null
}

export async function getPoReceiptHistory(poId?: string): Promise<PoReceiptHistoryRow[]> {
  const qs = poId ? `?po_id=${encodeURIComponent(poId)}` : ''
  const res = await apiFetch(`/api/purchase-orders-receipts/history${qs}`)
  const body = await parseJson<{ ok: boolean; data: PoReceiptHistoryRow[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải lịch sử nhận hàng')
  return (body as { data: PoReceiptHistoryRow[] }).data
}

// ─── RECEIVING & RECEIPTS ────────────────────────────────────────

export async function receivePurchaseOrder(
  id: string,
  payload: ReceivePoPayload,
  idempotencyKey?: string,
): Promise<ReceiptDto> {
  const key = idempotencyKey || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `idem-${Date.now()}`)
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(id)}/receipts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': key,
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: ReceiptDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể ghi nhận hàng cho PO ${id}`)
  return (body as { data: ReceiptDto }).data
}

export async function recordLineShort(
  poId: string,
  lineId: string,
  payload: RecordShortPayload,
): Promise<{ ok: true; po_line_id: string; short_qty: number; action: string }> {
  const res = await apiFetch(`/api/purchase-orders/${encodeURIComponent(poId)}/lines/${encodeURIComponent(lineId)}/short`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: true; data: { po_line_id: string; short_qty: number; action: string } } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể ghi nhận thiếu hàng cho dòng ${lineId}`)
  return (body as { data: { po_line_id: string; short_qty: number; action: string } }).data as any
}

export async function listReceipts(filter?: {
  po_id?: string
  limit?: number
  offset?: number
}): Promise<ReceiptDto[]> {
  const params = new URLSearchParams()
  if (filter?.po_id) params.set('po_id', filter.po_id)
  if (filter?.limit != null) params.set('limit', String(filter.limit))
  if (filter?.offset != null) params.set('offset', String(filter.offset))

  const qs = params.toString()
  const res = await apiFetch(`/api/receipts${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: boolean; data: ReceiptDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải danh sách phiếu nhập')
  return (body as { data: ReceiptDto[] }).data
}

export async function getReceipt(id: string): Promise<ReceiptDto | null> {
  const res = await apiFetch(`/api/receipts/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: ReceiptDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải phiếu nhập ${id}`)
  return (body as { data: ReceiptDto }).data
}

export async function patchReceipt(
  id: string,
  payload: { note?: string; notes?: string; lines?: Array<{ id: string; qty?: number; received_qty?: number }> },
  reason?: string,
): Promise<ReceiptDto> {
  const bodyPayload = {
    note: payload.note ?? payload.notes,
    lines: payload.lines?.map((l) => ({
      id: l.id,
      qty: l.qty ?? l.received_qty ?? 0,
      received_qty: l.qty ?? l.received_qty ?? 0,
    })),
    reason,
  }
  const res = await apiFetch(`/api/receipts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bodyPayload),
  })
  const body = await parseJson<{ ok: boolean; data: ReceiptDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chỉnh sửa phiếu nhập ${id}`)
  return (body as { data: ReceiptDto }).data
}

export async function voidReceipt(
  id: string,
  reason: string,
): Promise<ReceiptDto> {
  const res = await apiFetch(`/api/receipts/${encodeURIComponent(id)}/void`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: ReceiptDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể hủy phiếu nhập ${id}`)
  return (body as { data: ReceiptDto }).data
}

export async function getReceiptHistory(id: string): Promise<DocumentChangeLogDto[]> {
  const res = await apiFetch(`/api/receipts/${encodeURIComponent(id)}/history`)
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải lịch sử phiếu nhập ${id}`)
  return (body as { data: DocumentChangeLogDto[] }).data
}

// ─── PURCHASE REQUESTS ───────────────────────────────────────────

export async function listPurchaseRequests(filter?: {
  status?: PrStatus
  limit?: number
  offset?: number
}): Promise<PurchaseRequestDto[]> {
  const params = new URLSearchParams()
  if (filter?.status) params.set('status', filter.status)
  if (filter?.limit != null) params.set('limit', String(filter.limit))
  if (filter?.offset != null) params.set('offset', String(filter.offset))

  const qs = params.toString()
  const res = await apiFetch(`/api/purchase-requests${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải danh sách PR')
  return (body as { data: PurchaseRequestDto[] }).data
}

export async function getPurchaseRequest(id: string): Promise<PurchaseRequestDto | null> {
  const res = await apiFetch(`/api/purchase-requests/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải PR ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

// Hiện không có entry point UI nào gọi hàm này — PR được tạo duy nhất qua convert-từ-Suggestion. Giữ lại cho khả năng dùng sau này, không xóa.
export async function createPurchaseRequest(
  payload: CreatePrPayload,
): Promise<PurchaseRequestDto> {
  const res = await apiFetch('/api/purchase-requests', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tạo yêu cầu mua hàng')
  return (body as { data: PurchaseRequestDto }).data
}

export async function submitPurchaseRequest(id: string): Promise<PurchaseRequestDto> {
  const res = await apiFetch(`/api/purchase-requests/${encodeURIComponent(id)}/submit`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể gửi duyệt PR ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

export async function approvePurchaseRequest(
  id: string,
  reason?: string,
): Promise<PurchaseRequestDto> {
  const res = await apiFetch(`/api/purchase-requests/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể duyệt PR ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

export async function rejectPurchaseRequest(
  id: string,
  reason: string,
): Promise<PurchaseRequestDto> {
  const res = await apiFetch(`/api/purchase-requests/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ reason }),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể từ chối PR ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

export async function convertPurchaseRequest(
  id: string,
  payload: ConvertPrPayload,
): Promise<{ message: string; purchase_orders: PurchaseOrderDto[] }> {
  const res = await apiFetch(`/api/purchase-requests/${encodeURIComponent(id)}/convert`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: { message: string; purchase_orders: PurchaseOrderDto[] } } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chuyển PR ${id} thành PO`)
  return (body as { data: { message: string; purchase_orders: PurchaseOrderDto[] } }).data
}

export async function patchPurchaseRequest(
  id: string,
  payload: PatchPrHeaderPayload,
): Promise<PurchaseRequestDto> {
  const bodyPayload = {
    ...payload,
    note: payload.note ?? payload.notes,
  }
  const res = await apiFetch(`/api/purchase-requests/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bodyPayload),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chỉnh sửa PR ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

export async function createPrLine(
  id: string,
  payload: CreatePrLinePayload,
): Promise<PurchaseRequestDto> {
  const bodyPayload = {
    ...payload,
    qty: payload.qty ?? payload.requested_qty ?? (payload as any).quantity,
    note: payload.note ?? payload.notes,
  }
  const res = await apiFetch(`/api/purchase-requests/${encodeURIComponent(id)}/lines`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bodyPayload),
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể thêm dòng vào PR ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

export async function patchPrLine(
  id: string,
  lineId: string,
  payload: PatchPrLinePayload,
): Promise<PurchaseRequestDto> {
  const bodyPayload = {
    ...payload,
    qty: payload.qty ?? payload.requested_qty ?? (payload as any).quantity,
    note: payload.note ?? payload.notes,
  }
  const res = await apiFetch(
    `/api/purchase-requests/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyPayload),
    },
  )
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể chỉnh sửa dòng ${lineId} của PR ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

export async function deletePrLine(
  id: string,
  lineId: string,
  reason = 'Xóa dòng PR',
): Promise<PurchaseRequestDto> {
  const res = await apiFetch(
    `/api/purchase-requests/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
  )
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể xóa dòng ${lineId} của PR ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

export async function getPrHistory(id: string): Promise<DocumentChangeLogDto[]> {
  const res = await apiFetch(`/api/purchase-requests/${encodeURIComponent(id)}/history`)
  const body = await parseJson<{ ok: boolean; data: DocumentChangeLogDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải lịch sử thay đổi của PR ${id}`)
  return (body as { data: DocumentChangeLogDto[] }).data
}

// ─── PURCHASE SUGGESTIONS ────────────────────────────────────────

export async function listSuggestions(status?: SuggestionStatus): Promise<PurchaseSuggestionDto[]> {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  const qs = params.toString()
  const res = await apiFetch(`/api/suggestions${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: boolean; data: PurchaseSuggestionDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tải đề xuất mua')
  return (body as { data: PurchaseSuggestionDto[] }).data
}

export async function refreshSuggestions(): Promise<RefreshSuggestionsResult> {
  const res = await apiFetch('/api/suggestions/refresh', { method: 'POST' })
  const body = await parseJson<{
    ok: boolean
    data: PurchaseSuggestionDto[]
    skipped_missing_base_uom?: string[]
  } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Không thể tính lại đề xuất mua')
  const okBody = body as { data: PurchaseSuggestionDto[]; skipped_missing_base_uom?: string[] }
  return {
    suggestions: okBody.data,
    skipped_missing_base_uom: okBody.skipped_missing_base_uom ?? [],
  }
}

export async function dismissSuggestion(id: string): Promise<void> {
  const res = await apiFetch(`/api/suggestions/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: boolean } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể bỏ qua đề xuất ${id}`)
}

export async function convertSuggestionToPr(
  id: string,
): Promise<PurchaseRequestDto> {
  const res = await apiFetch(`/api/suggestions/${encodeURIComponent(id)}/convert-to-pr`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: boolean; data: PurchaseRequestDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tạo PR từ đề xuất ${id}`)
  return (body as { data: PurchaseRequestDto }).data
}

// ─── UOM FACTORS ─────────────────────────────────────────────────

export async function listMaterialUomFactors(materialId: string): Promise<UomFactorDto[]> {
  const res = await apiFetch(`/api/materials/${encodeURIComponent(materialId)}/uom-factors`)
  const body = await parseJson<{ ok: boolean; data: UomFactorDto[] } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể tải hệ số quy đổi vật tư ${materialId}`)
  return (body as { data: UomFactorDto[] }).data
}

export async function upsertMaterialUomFactor(
  materialId: string,
  payload: {
    supplier_id: string
    uom: string
    mode: 'FIXED' | 'FROM_DIMS'
    factor?: number | null
  },
): Promise<UomFactorDto> {
  const res = await apiFetch(`/api/materials/${encodeURIComponent(materialId)}/uom-factors`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: UomFactorDto } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể lưu hệ số quy đổi vật tư ${materialId}`)
  return (body as { data: UomFactorDto }).data
}

export async function deleteMaterialUomFactor(
  materialId: string,
  supplierId: string,
  uom: string,
): Promise<void> {
  const res = await apiFetch(
    `/api/materials/${encodeURIComponent(materialId)}/uom-factors/${encodeURIComponent(supplierId)}/${encodeURIComponent(uom)}`,
    { method: 'DELETE' },
  )
  const body = await parseJson<{ ok: boolean } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, `Không thể xóa hệ số quy đổi`)
}
